import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import {
  actualizarProveedor,
  eliminarProveedor,
  getProveedores,
  insertarProveedor,
} from "../../../services/modules/Proveedores";

const Proveedores = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [proveedores, setProveedores] = useState([]);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    id_proveedor: null,
    nombre: "",
    nit: "",
    dpi: "",
    direccion: "",
    telefono: "",
  });

  const fetchProveedores = async () => {
    setLoading(true);
    try {
      const data = await getProveedores();
      setProveedores(Array.isArray(data) ? data : []);
      setFilteredProveedores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar proveedores:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProveedores();
  }, []);

  useEffect(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) {
      setFilteredProveedores(proveedores);
      return;
    }
    setFilteredProveedores(
      (proveedores || []).filter((p) =>
        [p.nombre, p.nit, p.dpi, p.telefono]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    );
  }, [search, proveedores]);

  const handleOpenModal = (proveedor = null) => {
    if (proveedor) {
      setFormData({
        id_proveedor: proveedor.id_proveedor ?? null,
        nombre: proveedor.nombre ?? "",
        nit: proveedor.nit ?? "",
        dpi: proveedor.dpi ?? "",
        direccion: proveedor.direccion ?? "",
        telefono: proveedor.telefono ?? "",
      });
    } else {
      setFormData({
        id_proveedor: null,
        nombre: "",
        nit: "",
        dpi: "",
        direccion: "",
        telefono: "",
      });
    }
    setOpen(true);
  };

  const handleCloseModal = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (formData.id_proveedor) {
        await actualizarProveedor(formData);
        alert(`Proveedor ${formData.nombre} actualizado.`);
      } else {
        await insertarProveedor(formData);
        alert(`Proveedor ${formData.nombre} agregado.`);
      }
      await fetchProveedores();
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar proveedor:", error);
      alert("Ocurrio un error al guardar el proveedor.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Estas seguro de que deseas eliminar este proveedor?")) {
      try {
        await eliminarProveedor(id);
        alert("Proveedor eliminado exitosamente.");
        await fetchProveedores();
      } catch (error) {
        console.error("Error al eliminar proveedor:", error);
        alert("Ocurrio un error al eliminar el proveedor.");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "proveedor",
        headerName: "Proveedor",
        flex: 1.6,
        minWidth: 220,
        sortable: true,
        valueGetter: (_value, row) => row?.nombre || "",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36 }}>
              {row.nombre?.[0]?.toUpperCase() || "?"}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {row.nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                NIT {row.nit || "N/D"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "contacto",
        headerName: "Contacto",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row) => row?.telefono || "",
        renderCell: ({ row }) => (
          <Stack spacing={0.5}>
            <Typography variant="body2">{row.telefono || "Sin telefono"}</Typography>
            <Typography variant="caption" color="text.secondary">
              DPI {row.dpi || "N/D"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "direccion",
        headerName: "Dirección",
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        valueGetter: (_value, row) => row?.direccion || "",
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {row.direccion || "Sin direccion"}
          </Typography>
        ),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => handleOpenModal(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(row.id_proveedor)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <PageContainer>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            placeholder="Buscar por nombre, NIT, DPI o telefono"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setSearch("")}>
              Limpiar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
              Nuevo proveedor
            </Button>
          </Stack>
        </Stack>

        {isXs ? (
          <Stack spacing={1.5}>
            {(loading ? Array.from({ length: 3 }) : filteredProveedores).map((row, index) =>
              loading ? (
                <Paper key={index} sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                  <Skeleton variant="rounded" height={120} />
                </Paper>
              ) : (
                <Paper
                  key={row.id_proveedor}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {row.nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        NIT {row.nit || "N/D"} • DPI {row.dpi || "N/D"}
                      </Typography>
                      <Typography variant="body2">{row.telefono || "Sin telefono"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.direccion || "Sin direccion"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenModal(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id_proveedor)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              )
            )}
          </Stack>
        ) : (
          <AppDataTable
            rows={filteredProveedores}
            columns={columns}
            loading={loading}
          />
        )}
      </Stack>

      {!isXs && (
        <Fab
          color="primary"
          onClick={() => handleOpenModal()}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            boxShadow: 6,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <Dialog open={open} onClose={handleCloseModal} fullScreen={isXs} fullWidth maxWidth="sm">
        <DialogTitle>
          {formData.id_proveedor ? "Editar Proveedor" : "Ingresar Proveedor"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
          />
          <TextField
            label="NIT"
            name="nit"
            value={formData.nit}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
          />
          <TextField
            label="DPI"
            name="dpi"
            value={formData.dpi}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
          />
          <TextField
            label="Direccion"
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
          />
          <TextField
            label="Telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {formData.id_proveedor ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Proveedores;
